import React from 'react';

import { Divider, FormHelperText, Grid, Typography } from '@mui/material';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import { useRouter } from 'next/router';
import useAnalytics from 'src/analytics/useAnalytics';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { oauth } from 'src/services/auth-service/oauth.service';

interface Props {
  isSignup: boolean | null;
  referralCode?: string;
}

const SocialSignupAndLogin: React.FC<Props> = ({ isSignup, referralCode }) => {
  const { tenantId } = useNonNullContext(UserContext);
  const { setUser } = useNonNullContext(UserContext);
  const { track } = useAnalytics();
  const router = useRouter();
  const isAuthPage =
    router.pathname === '/signup' || router.pathname === '/login';
  const { publicRuntimeConfig } = getNextConfigValues();

  const [oauthErrorHelperText, setOauthErrorHelperText] = React.useState('');

  const onOAuthSignIn = async (
    response: google.accounts.id.CredentialResponse,
  ) => {
    const result = await oauth({
      oauthToken: response.credential,
      referralCode,
    });

    if (result.ok) {
      if (isSignup) {
        track('Account Created', {
          location: 'signup',
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          userId: result.user.id,
          tenantId,
        });
      }
      setUser(result.user);
      if (isAuthPage) {
        const redirectUrl = isSignup
          ? '/get-started'
          : publicRuntimeConfig.frontendUrls?.customerDashboard;
        router.push(redirectUrl);
      }
    } else {
      // The service already logs the failure; we just surface a generic
      // helper-text message to the user.
      const operation = isSignup ? 'sign up' : 'log in';
      setOauthErrorHelperText(
        `An error occurred when attempting to ${operation} with Google. Please try again or use email to continue.`,
      );
    }
  };

  const GOOGLE_OAUTH_CONTAINER_ID = 'g-signin2';

  React.useEffect(() => {
    const googleApi = window?.google;
    if (!googleApi) {
      // Logged at info level (not error) because this fires routinely for
      // bots, ad-blocker users, and clients on slow networks where the GSI
      // script hasn't loaded yet — not actionable as an alert.
      logger.info({
        msg: 'Google api for OAuth not available. Script not loaded correctly.',
      });
      return;
    }
    google.accounts.id.initialize({
      client_id: publicRuntimeConfig.GOOGLE_CLIENT_ID,
      ux_mode: 'popup',
      callback: onOAuthSignIn,
    });
    const element = document.getElementById(GOOGLE_OAUTH_CONTAINER_ID);
    google.accounts.id.renderButton(element!, {
      type: 'standard',
      text: isSignup ? 'signup_with' : 'signin_with',
      logo_alignment: 'left',
      width: 300,
    });
  }, []);

  return (
    <Grid
      container
      spacing={0}
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '1rem',
      }}
    >
      {isSignup && (
        <Grid>
          <Divider
            sx={{
              width: '15.5rem',
              marginTop: '1.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <Typography variant="overline">OR</Typography>
          </Divider>
        </Grid>
      )}
      <Grid sx={isSignup ? undefined : { paddingTop: '1.5rem' }}>
        <div id={GOOGLE_OAUTH_CONTAINER_ID}></div>
      </Grid>
      {oauthErrorHelperText && (
        <Grid>
          <FormHelperText
            error
            sx={{ textAlign: 'center', paddingTop: '0.5rem' }}
          >
            {oauthErrorHelperText}
          </FormHelperText>
        </Grid>
      )}
      {!isSignup && (
        <Grid>
          <Divider sx={{ width: '15.5rem', marginTop: '1.5rem' }}>
            <Typography variant="overline">OR</Typography>
          </Divider>
        </Grid>
      )}
    </Grid>
  );
};

export default SocialSignupAndLogin;
